import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Button } from "./ui/button";
import { ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth";

/**
 * Iter37 Vague 2 — Blocking banner shown to authenticated users when CGV/CGU
 * has been updated and they have not yet re-accepted the new version.
 * Backend: GET /api/legal/me/status + POST /api/legal/me/accept
 */
function buildAcceptPayload(items) {
  const payload = {};
  if (items?.cgu?.needs_reaccept) payload.cgu_version = items.cgu.latest_version;
  if (items?.cgv?.needs_reaccept) payload.cgv_version = items.cgv.latest_version;
  return payload;
}

function buildBannerMessage(needCgu, needCgv) {
  if (needCgu && needCgv) return "Nos CGU et CGV ont été mises à jour.";
  if (needCgu) return "Nos CGU ont été mises à jour.";
  return "Nos CGV ont été mises à jour.";
}

function BannerLinks({ needCgu, needCgv }) {
  return (
    <>
      {needCgu && <Link to="/legal/cgu" className="underline font-medium" data-testid="legal-link-cgu">Lire les CGU</Link>}
      {needCgu && needCgv && " · "}
      {needCgv && <Link to="/legal/cgv" className="underline font-medium" data-testid="legal-link-cgv">Lire les CGV</Link>}
    </>
  );
}

export default function LegalReacceptBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    api.get("/legal/me/status")
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const accept = async () => {
    if (busy || !status?.items) return;
    setBusy(true);
    try {
      await api.post("/legal/me/accept", buildAcceptPayload(status.items));
      toast.success("Merci, conditions acceptées.");
      setStatus({ ...status, needs_reaccept: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  if (!user || !status?.needs_reaccept) return null;
  const items = status.items || {};
  const needCgu = !!items.cgu?.needs_reaccept;
  const needCgv = !!items.cgv?.needs_reaccept;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 border-b-2 border-amber-700 text-white shadow-lg"
      data-testid="legal-reaccept-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
        <ShieldAlert className="w-5 h-5 shrink-0" />
        <div className="flex-1 min-w-[260px] text-sm">
          <strong>Mise à jour des conditions :</strong>{" "}
          {buildBannerMessage(needCgu, needCgv)}{" "}
          Vous devez les relire et les accepter pour continuer.{" "}
          <BannerLinks needCgu={needCgu} needCgv={needCgv} />
        </div>
        <Button
          onClick={accept}
          disabled={busy}
          className="rounded-full bg-white text-amber-700 hover:bg-amber-50 font-semibold"
          data-testid="legal-reaccept-btn"
        >
          {busy ? "Validation…" : "J'accepte les nouvelles conditions"}
        </Button>
      </div>
    </div>
  );
}
